import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { fmtN, fmtRelative, cn } from '@/lib/utils';
import { PageHeader } from '@/components/PageHeader';
import { Pagination } from '@/components/Pagination';

type TripRow = {
  id: string;
  name: string;
  destinationCountry: string | null;
  startDate: string | null;
  endDate: string | null;
  status: string;
  type: string | null;
  pace: string | null;
  budget: string | null;
  coverImageUrl: string | null;
  createdBy: string;
  createdAt: string;
};
type Env = { rows: TripRow[]; page: number; pageSize: number; total: number };

const STATUSES = ['', 'draft', 'active', 'completed', 'cancelled'] as const;

export function Trips() {
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<(typeof STATUSES)[number]>('');
  const [q, setQ] = useState('');

  const { data, isLoading } = useQuery<Env>({
    queryKey: ['/admin/api/trips', page, status, q],
    queryFn: () => {
      const params = new URLSearchParams({ page: String(page), pageSize: '50' });
      if (status) params.set('status', status);
      if (q) params.set('q', q);
      return api.get<Env>(`/admin/api/trips?${params}`);
    },
  });

  return (
    <div>
      <PageHeader title="Trips" subtitle={data ? `${fmtN(data.total)} total` : 'Loading…'} />

      <div className="ad-card p-3 mb-3 flex flex-wrap gap-2 items-center">
        <input
          className="ad-input flex-1 min-w-[200px]"
          placeholder="Search trip name…"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setPage(1);
          }}
        />
        <div className="flex gap-1 items-center">
          {STATUSES.map((s) => (
            <button
              key={s || 'all'}
              onClick={() => {
                setStatus(s);
                setPage(1);
              }}
              className={cn(
                'ad-btn-sm',
                status === s ? 'bg-accent/20 text-accent border border-accent/40' : 'ad-btn-secondary',
              )}
            >
              {s || 'All'}
            </button>
          ))}
        </div>
      </div>

      <div className="ad-card overflow-hidden">
        <table className="ad-table">
          <thead>
            <tr>
              <th>Trip</th>
              <th>Dates</th>
              <th>Status</th>
              <th>Type</th>
              <th>Pace</th>
              <th>Budget</th>
              <th>Created</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={8} className="text-center text-text3 py-10">
                  Loading…
                </td>
              </tr>
            ) : data && data.rows.length === 0 ? (
              <tr>
                <td colSpan={8} className="text-center text-text3 py-10">
                  No trips.
                </td>
              </tr>
            ) : (
              data?.rows.map((t) => (
                <tr key={t.id}>
                  <td>
                    <div className="font-medium">{t.name}</div>
                    <div className="text-text3 text-xs">{t.destinationCountry}</div>
                  </td>
                  <td className="text-text2">
                    {t.startDate?.slice(0, 10)} → {t.endDate?.slice(0, 10)}
                  </td>
                  <td>
                    <span
                      className={cn(
                        'ad-badge',
                        t.status === 'active'
                          ? 'ad-badge-success'
                          : t.status === 'draft'
                            ? 'ad-badge-warning'
                            : 'ad-badge-neutral',
                      )}
                    >
                      {t.status}
                    </span>
                  </td>
                  <td className="text-text2">{t.type ?? '—'}</td>
                  <td className="text-text2">{t.pace ?? '—'}</td>
                  <td className="text-text2">{t.budget ?? '—'}</td>
                  <td className="text-text2">{fmtRelative(t.createdAt)}</td>
                  <td className="text-right">
                    <Link to={`/trips/${t.id}`} className="text-accent hover:underline text-xs">
                      View
                    </Link>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {data && (
        <Pagination page={data.page} pageSize={data.pageSize} total={data.total} onPage={setPage} />
      )}
    </div>
  );
}
